import type { LocationQueryRaw } from 'vue-router'

export const REMOTE_DECK_CONTROLS_CONTEXT_NAME = 'RemoteDeckControls'
export const REMOTE_DECK_CONTROLS_CONFIG = 'popup,width=640,height=480,left=1200,top=600'
export const REMOTE_DECK_CONTROLS_PATHNAME = '/decks/remote-deck-controls'

export enum RemoteControlMessagesIn {
  SetDeck = 'set-deck',
  SetSlide = 'set-slide',
  Recording = 'recording',
}

export enum RemoteControlMessagesOut {
  Loaded = 'loaded',
  SetSlide = 'set-slide',
  SetSlideId = 'set-slide-id',
  AnnotationActive = 'annotation:active',
  AnnotationStroke = 'annotation:stroke',
  AnnotationReset = 'annotation:reset',
  AnnotationUndo = 'annotation:undo',
  Start = 'record:start',
  Stop = 'record:stop',
}

export interface UseDeckRecorder {
  openRemoteDeckControls(config?: DeckControlsParams): WindowProxy | null
  startRecording(): Promise<{ stream: MediaStream | null; recorder: MediaRecorder | null }>
  stopRecording(): void
}

interface DeckControlsParams extends LocationQueryRaw {
  slide?: number
}

const RECORDING_BIT_RATE = 10000000 // 10Mb/s
const RECORDING_CODECS = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm;codecs=av1',
  'video/webm',
  'video/mp4;codecs=avc1',
  'video/mp4',
]

function findBestSupportedCodec(): string {
  for (const codec of RECORDING_CODECS) {
    if (MediaRecorder.isTypeSupported(codec)) {
      return codec
    }
  }

  throw new Error('Browser not supported')
}

export function useDeckRecorder(): UseDeckRecorder {
  const controls = ref<WindowProxy | null>(null)
  const stream = ref<MediaStream | null>(null)
  const recorder = ref<MediaRecorder | null>(null)
  function openRemoteDeckControls(config?: DeckControlsParams): WindowProxy | null {
    const router = useRouter()
    const controlsRoute = router.resolve({ path: '/decks/remote-deck-controls', query: config })
    controls.value = window.open(controlsRoute.href, REMOTE_DECK_CONTROLS_CONTEXT_NAME, REMOTE_DECK_CONTROLS_CONFIG)
    return controls.value
  }

  async function startRecording(): Promise<{ stream: MediaStream | null; recorder: MediaRecorder | null }> {
    const displayMediaOptions = {
      video: {
        displaySurface: 'browser',
        frameRate: { ideal: 60, max: 60 },
        width: { ideal: 1920, max: 7680 },
        height: { ideal: 1080, max: 4320 },
        resizeMode: 'none',
        cursor: 'never',
      },
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      systemAudio: 'exclude',
      surfaceSwitching: 'exclude',
      monitorTypeSurfaces: 'exclude',
    }

    try {
      const codec = findBestSupportedCodec()
      stream.value = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions)
      recorder.value = new MediaRecorder(stream.value, {
        mimeType: codec,
        videoBitsPerSecond: RECORDING_BIT_RATE,
      })

      const chunks: Blob[] = []
      recorder.value.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) {
          chunks.push(e.data)
        }
      }

      recorder.value.onstart = () => {
        controls.value?.postMessage({ name: RemoteControlMessagesIn.Recording, payload: true }, window.location.origin)
      }

      recorder.value.onstop = () => {
        downloadVideoResult(chunks, codec)
        controls.value?.postMessage({ name: RemoteControlMessagesIn.Recording, payload: false }, window.location.origin)
      }

      stream.value.getTracks().forEach(track => {
        track.onended = () => recorder.value?.stop()
      })

      recorder.value.start()
    } catch (e) {
      throw e
    }

    return { stream: stream.value, recorder: recorder.value }
  }

  function stopRecording(): void {
    if (stream.value == null) return
    stream.value.getTracks().forEach(track => track.stop())
  }

  function downloadVideoResult(result: Blob[], mimeType: string) {
    const blob = new Blob(result, { type: mimeType })
    const extension = mimeType.includes('video/webm') ? 'webm' : 'mp4'
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `recording.${extension}`
    a.click()

    URL.revokeObjectURL(url)
  }

  return {
    openRemoteDeckControls,
    startRecording,
    stopRecording,
  }
}
